import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Copy, Users, Trash2, Eye } from "lucide-react";

const METHODS = [["teams", "Microsoft Teams"], ["ringcentral", "RingCentral"], ["manual_video", "Manual Video Link"], ["in_person", "In Person"], ["phone", "Phone"]];

export default function GroupSessions() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [roster, setRoster] = useState(null);

  const load = () => api.get(`/scheduling-pages/${id}/group-sessions`).then((r) => setItems(r.data.items)).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    const body = { title: edit.title, description: edit.description, method: edit.method, location: edit.location,
      start: edit.start ? new Date(edit.start).toISOString() : "", duration_min: Number(edit.duration_min), capacity: Number(edit.capacity), show_seats: !!edit.show_seats };
    try { await api.post(`/scheduling-pages/${id}/group-sessions`, body); toast.success("Group session created"); setEdit(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const copyLink = (u) => { navigator.clipboard?.writeText(u); toast.success("Hidden link copied"); };
  const remove = async (g) => { try { await api.delete(`/scheduling-pages/${id}/group-sessions/${g.id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const openRoster = async (g) => { try { const { data } = await api.get(`/scheduling-pages/${id}/group-sessions/${g.id}/roster`); setRoster(data); } catch (e) { toast.error(apiError(e)); } };

  if (!items) return <p className="text-slate-400 py-16 text-center">Loading…</p>;
  return (
    <div className="max-w-3xl">
      <button onClick={() => navigate("/calendar/scheduling-pages")} className="text-sm text-slate-400 hover:text-slate-600 inline-flex items-center gap-1 mb-2" data-testid="groups-back"><ArrowLeft className="w-4 h-4" />Scheduling Pages</button>
      <PageHeader title="Group Sessions" description="One session with multiple candidate reservations. Share the hidden link — it never appears on your public page."
        actions={<Button onClick={() => setEdit({ title: "Group Session", description: "", method: "teams", location: "", start: "", duration_min: 60, capacity: 10, show_seats: true })} className="bg-brand hover:bg-brand-strong" data-testid="group-create"><Plus className="w-4 h-4 mr-1" />New Group Session</Button>} />

      {items.length === 0 ? <p className="text-slate-400 py-12 text-center" data-testid="groups-empty">No group sessions yet.</p> : (
        <div className="space-y-3" data-testid="group-sessions-list">
          {items.map((g) => (
            <div key={g.id} className="rounded-xl border border-slate-200 bg-white shadow-sm p-4" data-testid={`group-row-${g.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">{g.title}</p>
                  <p className="text-sm text-slate-400">{new Date(g.start).toLocaleString()} · {g.duration_min} min</p>
                  <p className="text-xs text-slate-500 mt-1"><Users className="w-3.5 h-3.5 inline mr-1" />{g.seats_taken}/{g.capacity} reserved</p>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${g.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{g.active ? "Active" : "Inactive"}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => copyLink(g.hidden_url)} data-testid={`group-copy-${g.id}`}><Copy className="w-3.5 h-3.5 mr-1" />Copy Hidden Link</Button>
                <Button size="sm" variant="outline" onClick={() => window.open(g.hidden_url, "_blank")} data-testid={`group-preview-${g.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Preview</Button>
                <Button size="sm" variant="outline" onClick={() => openRoster(g)} data-testid={`group-roster-${g.id}`}><Users className="w-3.5 h-3.5 mr-1" />Roster</Button>
                <Button size="sm" variant="outline" onClick={() => remove(g)} className="text-rose-500" data-testid={`group-delete-${g.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="group-editor">
          <DialogHeader><DialogTitle>New Group Session</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <Fld label="Title"><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="grp-title" /></Fld>
              <Fld label="Description"><Textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="grp-desc" /></Fld>
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Start"><Input type="datetime-local" value={edit.start} onChange={(e) => setEdit({ ...edit, start: e.target.value })} data-testid="grp-start" /></Fld>
                <Fld label="Duration (min)"><Input type="number" value={edit.duration_min} onChange={(e) => setEdit({ ...edit, duration_min: e.target.value })} data-testid="grp-duration" /></Fld>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Capacity"><Input type="number" min={1} value={edit.capacity} onChange={(e) => setEdit({ ...edit, capacity: e.target.value })} data-testid="grp-capacity" /></Fld>
                <Fld label="Method">
                  <Select value={edit.method} onValueChange={(v) => setEdit({ ...edit, method: v })}>
                    <SelectTrigger data-testid="grp-method"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{METHODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></Fld>
              </div>
              <Fld label="Location / Instructions"><Input value={edit.location} onChange={(e) => setEdit({ ...edit, location: e.target.value })} data-testid="grp-location" /></Fld>
              <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5"><span className="text-sm text-slate-600">Show remaining seats publicly</span><Switch checked={!!edit.show_seats} onCheckedChange={(v) => setEdit({ ...edit, show_seats: v })} data-testid="grp-showseats" /></label>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} className="bg-brand hover:bg-brand-strong" data-testid="grp-save" disabled={!edit?.start}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!roster} onOpenChange={(o) => !o && setRoster(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="group-roster-dialog">
          <DialogHeader><DialogTitle>Attendees (internal only)</DialogTitle></DialogHeader>
          {roster && (
            <div>
              <p className="text-sm text-slate-500 mb-2">{roster.seats_taken}/{roster.capacity} reserved</p>
              <div className="space-y-2">
                {roster.attendees.length === 0 ? <p className="text-sm text-slate-400">No reservations yet.</p> : roster.attendees.map((a) => (
                  <div key={a.person_id} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between" data-testid={`roster-${a.person_id}`}>
                    <div><button onClick={() => navigate(`/people/detail/${a.person_id}`)} className="text-sm font-medium text-brand hover:underline">{a.name}</button>
                      <p className="text-xs text-slate-400">{a.referral_partner ? `Referred by ${a.referral_partner}` : "Direct"}{a.prescreen_completed ? " · Pre-Screen ✓" : ""}</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Fld({ label, children }) { return <div><Label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</Label>{children}</div>; }
